import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';

type GenerateMeetingAgendaPayload = {
  templateId?: string;
};

type GenerateMeetingAgendaResponse = {
  taskId: string;
  meetingAgendaId: string;
  fileOwnerClientId: string;
};

type MutationInput = {
  resource: string;
  values: GenerateMeetingAgendaPayload;
};

export function useGenerateMeetingAgenda() {
  const queryClient = useQueryClient();

  const {
    mutateAsync,
    error,
    isPending: isLoading,
    reset,
  } = useMutation<{ data: GenerateMeetingAgendaResponse }, Error, MutationInput>({
    mutationFn: ({ resource, values }: MutationInput) =>
      api.post<GenerateMeetingAgendaResponse>(resource, values),
    onSettled: (response, _error, { resource }) => {
      queryClient.invalidateQueries({ queryKey: resource.split('/') });
      const [scope, id] = resource.split('/');
      queryClient.invalidateQueries({ queryKey: [scope, id, 'all-documents'] });
      if (response) {
        queryClient.invalidateQueries({
          queryKey: ['clients', response.data.fileOwnerClientId, 'meeting-agendas'],
        });
      }
    },
  });

  const generateClientMeetingAgenda = async (
    clientId: string,
    values: GenerateMeetingAgendaPayload,
  ) => {
    return mutateAsync({ resource: `clients/${clientId}/meeting-agendas`, values });
  };

  const generateFamilyMeetingAgenda = async (
    familyId: string,
    values: GenerateMeetingAgendaPayload,
  ) => {
    return mutateAsync({ resource: `families/${familyId}/meeting-agendas`, values });
  };

  const generateMeetingAgenda = async (
    id: string,
    type: 'client' | 'family',
    values: GenerateMeetingAgendaPayload,
  ) => {
    if (type === 'client') {
      return generateClientMeetingAgenda(id, values);
    } else {
      return generateFamilyMeetingAgenda(id, values);
    }
  };

  return {
    generateMeetingAgenda,
    error,
    isLoading,
    reset,
  };
}
